import React, { useEffect, useState, useRef } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Input,
    Spinner,
    Modal,
    ModalBody,
    Badge,
} from 'reactstrap';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import { toast } from 'react-toastify';
import moment from 'moment';
import Select from 'react-select';
import Loader from '../../../Layout/Loader';
import { isOwnRequest } from '../../../Utils/authUtils';

export default function PODApprovals() {
    const [territories, setTerritories] = useState<any[]>([]);
    const [selectedTerr, setSelectedTerr] = useState<string | number>('');
    const [selectedMonth, setSelectedMonth] = useState<string>(moment().format('YYYY-MM'));
    const [loading, setLoading] = useState<boolean>(false);
    const [productList, setProductList] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [productStatus, setProductStatus] = useState<any[]>([]);
    const [podStatusReason, setPodStatusReason] = useState<string>('');
    const [submittingAction, setSubmittingAction] = useState<boolean>(false);

    // Attachment Modal State
    const [attachmentModalOpen, setAttachmentModalOpen] = useState<boolean>(false);
    const [mediaLoading, setMediaLoading] = useState<boolean>(false);
    const [currentMedia, setCurrentMedia] = useState<{ images: any[]; pdfs: any[] }>({ images: [], pdfs: [] });
    const [selectedMediaTab, setSelectedMediaTab] = useState<'images' | 'pdfs'>('images');
    const mediaMetaCache = useRef<Map<string, any>>(new Map());

    const monthOptions = Array.from({ length: 6 }).map((_, i) => {
        const d = moment().subtract(i, 'months');
        return { label: d.format('MMM YYYY'), value: d.format('YYYY-MM') };
    });

    const currentData = productList[currentIndex] || null;

    const podQty = Number(currentData?.item?.qty) || 0;
    const pobQty = Number(currentData?.item?.totalQty) || 0;
    const podValue = Number(currentData?.item?.rate) || 0;
    const unitValue = podQty > 0 ? podValue / podQty : 0;
    const pobValue = unitValue * pobQty;

    const fetchTerritories = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        try {
            const url = `${serverUrl}${API.getTerritoriesArray}`;
            const response = await getData(url, true);
            let list: any[] = [];
            const d = response?.data?.data || response?.data;
            if (Array.isArray(d)) {
                list = d.filter(Boolean).map((t: any) => ({
                    label: t.label || t.name || t.Stownname || String(t.id ?? t.value ?? t.Itownid),
                    id: t.id ?? t.value ?? t.Itownid
                }));
            } else if (d && typeof d === 'object') {
                list = Object.keys(d).map((key) => ({ label: d[key], id: key }));
            }
            setTerritories(list);
            if (list.length > 0) {
                const userTerrId = localStorage.getItem("territoryId") || localStorage.getItem("territory_id");
                if (userTerrId && list.some((t: any) => String(t.id) === String(userTerrId))) {
                    setSelectedTerr(userTerrId);
                } else {
                    const firstId = list[0].id ?? list[0].value ?? list[0].Itownid;
                    setSelectedTerr(firstId);
                }
            }
        } catch (err) {
            console.error("Error fetching territories:", err);
        }
    };

    const fetchPodStatusReasons = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        try {
            const url = `${serverUrl}${API.getPodStatus}`;
            const response = await getData(url, true);
            const d = response?.data;
            if (response?.status === 200 && Array.isArray(d)) {
                const formatted = d.filter(Boolean).map((item: any) => ({
                    label: item.Reason || item.reason || item.label || 'Reason',
                    value: item.Reason || item.reason || item.value || 'Reason'
                }));
                setProductStatus(formatted);
            } else {
                setProductStatus([
                    { label: 'Invalid Stock / POD', value: 'Invalid Stock / POD' },
                    { label: 'Quantity Mismatch', value: 'Quantity Mismatch' },
                    { label: 'Incorrect Amount', value: 'Incorrect Amount' },
                    { label: 'Other', value: 'Other' },
                ]);
            }
        } catch (err) {
            console.error("Error fetching pod status reasons:", err);
            setProductStatus([
                { label: 'Invalid Stock / POD', value: 'Invalid Stock / POD' },
                { label: 'Quantity Mismatch', value: 'Quantity Mismatch' },
                { label: 'Incorrect Amount', value: 'Incorrect Amount' },
                { label: 'Other', value: 'Other' },
            ]);
        }
    };

    const fetchPodData = async (territory_id: string | number, month: string) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !territory_id) return;
        setLoading(true);
        try {
            const url = `${serverUrl}${API.getAllSubmittedPodLineItems}`;
            const response = await getData(url, true, { territory_id, month });

            if (response.status === 200 || response.data?.statusCode === 200) {
                const rawList = Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.data) ? response.data.data : []);
                const mappedData = rawList.filter(Boolean).map((item: any) => ({
                    chemist: item.BilledToOutletName || item.chemist || 'N/A',
                    supplier: item.BilledByOutletName || item.supplier || 'N/A',
                    month: item.MonthYear ? moment(item.MonthYear, ['YYYY-MM', 'YYYY-MM-DD']).format('MMMM YYYY') : month,
                    submittedBy: item.CreatedByName || item.submittedBy || 'N/A',
                    submittedDate: item.CreatedAt ? moment(item.CreatedAt).format('DD MMMM YYYY') : 'N/A',
                    item: {
                        name: item.ProductName || item.product_name || 'N/A',
                        qty: item.Qty || item.qty || 0,
                        totalQty: item.AllocatedQty || item.totalQty || 0,
                        rate: parseFloat(item.Rate || item.rate) || 0,
                    },
                    attachment: item?.Shippingorder?.InvoiceFile || item?.attachment || null,
                    solid: item.Solid || item.solid || item.id,
                    CreatedBy: item.CreatedBy ?? item.created_by,
                    CreatedByPosition: item.CreatedByPosition ?? item.created_by_position,
                    EmployeeId: item.EmployeeId ?? item.employee_id,
                }));

                setProductList(mappedData);
                setCurrentIndex(0);
            } else {
                setProductList([]);
            }
        } catch (err) {
            console.error("Error fetching POD data:", err);
            setProductList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTerritories();
        fetchPodStatusReasons();
    }, []);

    useEffect(() => {
        if (selectedTerr && selectedMonth) {
            fetchPodData(selectedTerr, selectedMonth);
        }
    }, [selectedTerr, selectedMonth]);

    const handleStatusUpdate = async (type: 'Approve' | 'Reject') => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !currentData?.solid) {
            toast.error("Missing required data to submit status update");
            return;
        }

        if (type === 'Reject' && !podStatusReason) {
            toast.error("Please select a reason to reject the POD");
            return;
        }

        setSubmittingAction(true);
        try {
            const url = `${serverUrl}${API.statusUpdate}`;
            const statusVal = type === 'Approve' ? 'approved' : 'reject';
            const remarkVal = type === 'Approve' ? '' : podStatusReason;

            const response = await postData(url, true, {
                solid: currentData.solid,
                status: statusVal,
                remark: remarkVal,
            });

            if (response.status === 200 || response.data?.statusCode === 200) {
                toast.success(`POD ${type}d successfully!`);
                setPodStatusReason('');
                if (productList.length > 1) {
                    const nextIdx = currentIndex < productList.length - 1 ? currentIndex + 1 : 0;
                    setCurrentIndex(nextIdx);
                    fetchPodData(selectedTerr, selectedMonth);
                } else {
                    fetchPodData(selectedTerr, selectedMonth);
                }
            } else {
                toast.error(response.data?.message || `Failed to ${type.toLowerCase()} POD`);
            }
        } catch (err: any) {
            console.error("Error updating status:", err);
            toast.error(err?.message || "An error occurred while updating status");
        } finally {
            setSubmittingAction(false);
        }
    };

    const resolveMediaByIds = async (rawAttachmentStr: string) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !rawAttachmentStr) return;
        setMediaLoading(true);
        const ids = String(rawAttachmentStr).split(',').map(s => s.trim()).filter(Boolean);
        const images: any[] = [];
        const pdfs: any[] = [];

        for (const id of ids) {
            try {
                let meta = mediaMetaCache.current.get(id);
                if (!meta) {
                    const url = `${serverUrl}${API.downloadFile}?mediaid=${id}`;
                    const res = await getData(url, true);
                    meta = res?.data || {};
                    mediaMetaCache.current.set(id, meta);
                }
                const isPdf = meta?.mediatype && String(meta.mediatype).toLowerCase().includes('pdf');
                const fileUrl = meta?.url || `${serverUrl}media?id=${id}`;
                if (isPdf) {
                    pdfs.push({ id, pdfurl: meta?.pdfurl || fileUrl, name: meta?.name || `Document-${id}` });
                } else {
                    images.push({ id, url: fileUrl });
                }
            } catch (e) {
                images.push({ id, url: `${serverUrl}media?id=${id}` });
            }
        }

        setCurrentMedia({ images, pdfs });
        setSelectedMediaTab(images.length > 0 ? 'images' : 'pdfs');
        setMediaLoading(false);
        setAttachmentModalOpen(true);
    };

    const terrOptions = React.useMemo(() => {
        if (!Array.isArray(territories)) return [];
        return territories.filter(Boolean).map((t: any) => {
            const val = t?.id ?? t?.value ?? t?.Itownid ?? '';
            const lbl = t?.label || t?.name || t?.Stownname || String(val || 'Unknown');
            return { value: String(val), label: String(lbl) };
        });
    }, [territories]);

    return (
        <div className="p-3" style={{ color: '#000000' }}>
            {/* Filter Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3 p-3 bg-light border rounded">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div style={{ minWidth: '260px' }}>
                        <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Select Territory</label>
                        <Select
                            options={terrOptions}
                            value={terrOptions.find((t: any) => String(t.value) === String(selectedTerr)) || null}
                            onChange={(opt: any) => setSelectedTerr(opt ? opt.value : '')}
                            isSearchable={true}
                            placeholder="Search Territory..."
                            className="text-dark fw-semibold"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderColor: '#dee2e6',
                                    minHeight: '38px',
                                    color: '#000000',
                                }),
                                singleValue: (base) => ({
                                    ...base,
                                    color: '#000000',
                                    fontWeight: '600',
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    color: '#000000',
                                    backgroundColor: state.isFocused ? '#e9ecef' : '#ffffff',
                                }),
                            }}
                        />
                    </div>

                    <div style={{ minWidth: '150px' }}>
                        <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Select Month</label>
                        <Input
                            type="select"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="bg-white border text-dark shadow-none fw-semibold"
                            style={{ fontSize: '0.9rem', color: '#000000' }}
                        >
                            {monthOptions.map((m, idx) => (
                                <option key={idx} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </Input>
                    </div>
                </div>

                {/* Pagination Controls */}
                {productList.length > 0 && (
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            color="dark"
                            outline
                            size="sm"
                            disabled={currentIndex === 0}
                            onClick={() => setCurrentIndex(prev => Math.max(prev - 1, 0))}
                            className="fw-bold"
                        >
                            <FeatherIconCom iconName="ChevronLeft" size={16} />
                        </Button>
                        <span className="fw-bold px-3 py-1 bg-white border rounded text-dark" style={{ fontSize: '0.95rem' }}>
                            {currentIndex + 1} / {productList.length}
                        </span>
                        <Button
                            color="dark"
                            outline
                            size="sm"
                            disabled={currentIndex >= productList.length - 1}
                            onClick={() => setCurrentIndex(prev => Math.min(prev + 1, productList.length - 1))}
                            className="fw-bold"
                        >
                            <FeatherIconCom iconName="ChevronRight" size={16} />
                        </Button>
                    </div>
                )}
            </div>

            {loading ? (
                <Loader height="250px" />
            ) : currentData ? (
                <div>
                    <Row className="g-4 mb-4">
                        {/* POD Overview */}
                        <Col md={7}>
                            <Card className="border shadow-sm h-100">
                                <CardBody className="p-4" style={{ color: '#000000' }}>
                                    <h5 className="fw-bold text-dark border-bottom pb-2 mb-3" style={{ color: '#000000' }}>
                                        <FeatherIconCom iconName="FileText" size={18} className="me-2 text-primary" />
                                        POD Overview
                                    </h5>
                                    <div className="p-3 bg-light border rounded">
                                        {[
                                            { label: 'Chemist', value: currentData?.chemist || 'N/A' },
                                            { label: 'Supplied by', value: currentData?.supplier || 'N/A' },
                                            { label: 'POD Month', value: currentData?.month || 'N/A' },
                                            { label: 'POD Submitted by', value: currentData?.submittedBy || 'N/A' },
                                            { label: 'POD Submitted at', value: currentData?.submittedDate || 'N/A' },
                                        ].map((row, idx) => (
                                            <div key={idx} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                                <span className="fw-bold text-dark">{row.label}:</span>
                                                <span className="fw-bold text-primary">{row.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Item Details */}
                        <Col md={5}>
                            <Card className="border shadow-sm h-100">
                                <CardBody className="p-4" style={{ color: '#000000' }}>
                                    <h5 className="fw-bold text-dark border-bottom pb-2 mb-3" style={{ color: '#000000' }}>
                                        <FeatherIconCom iconName="Package" size={18} className="me-2 text-primary" />
                                        Item Details
                                    </h5>
                                    <div className="p-3 bg-light border rounded">
                                        <h6 className="fw-bold text-dark mb-2">{currentData?.item?.name || 'N/A'}</h6>
                                        <div className="small text-dark mb-1 fw-semibold">
                                            Total POB: <strong>{pobQty} Qty</strong> | ₹{pobValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                        <div className="small text-dark mb-3 fw-semibold">
                                            Total POD: <strong>{podQty} Qty</strong> | ₹{podValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <Badge color="success" className="px-3 py-2 fs-6">
                                                POD Qty: {currentData?.item?.qty || 0} / {currentData?.item?.totalQty || 0}
                                            </Badge>
                                        </div>
                                    </div>

                                    {/* Attachment Section */}
                                    <div className="mt-4">
                                        <h6 className="fw-bold text-dark mb-2">Attachment</h6>
                                        {currentData.attachment ? (
                                            <Button
                                                color="primary"
                                                outline
                                                className="w-100 py-2 d-flex align-items-center justify-content-center gap-2 fw-bold"
                                                onClick={() => resolveMediaByIds(currentData.attachment)}
                                            >
                                                <FeatherIconCom iconName="Paperclip" size={16} />
                                                <span>View Attachment(s)</span>
                                            </Button>
                                        ) : (
                                            <div className="text-muted small italic p-2 border rounded bg-light text-center">
                                                No attachments provided
                                            </div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Actions Card */}
                    {!isOwnRequest(currentData) ? (
                    <Card className="border shadow-sm mb-4">
                        <CardBody className="p-4" style={{ color: '#000000' }}>
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Button
                                        color="success"
                                        className="w-100 py-2 fw-bold text-white border-0"
                                        disabled={submittingAction}
                                        style={{ backgroundColor: '#198754', fontSize: '1rem' }}
                                        onClick={() => handleStatusUpdate('Approve')}
                                    >
                                        {submittingAction ? <Spinner size="sm" color="light" /> : "Approve POD"}
                                    </Button>
                                </Col>

                                <Col md={5}>
                                    <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>POD Rejection Remark</label>
                                    <Input
                                        type="select"
                                        value={podStatusReason}
                                        onChange={(e) => setPodStatusReason(e.target.value)}
                                        className="bg-white border text-dark shadow-none fw-semibold py-2"
                                        style={{ fontSize: '0.9rem', color: '#000000' }}
                                    >
                                        <option value="">Select Reason for Rejection</option>
                                        {productStatus.map((st, idx) => (
                                            <option key={idx} value={st.value}>
                                                {st.label}
                                            </option>
                                        ))}
                                    </Input>
                                </Col>

                                <Col md={3}>
                                    <Button
                                        color="danger"
                                        className="w-100 py-2 fw-bold text-white border-0"
                                        disabled={submittingAction || !podStatusReason}
                                        style={{ backgroundColor: '#dc3545', fontSize: '1rem' }}
                                        onClick={() => handleStatusUpdate('Reject')}
                                    >
                                        {submittingAction ? <Spinner size="sm" color="light" /> : "Reject POD"}
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                    ) : (
                        <div className="p-3 bg-light-warning border border-warning rounded text-center fw-bold text-dark mb-4">
                            Approval controls are restricted for your own requests.
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center py-5 border rounded bg-white">
                    <FeatherIconCom iconName="Inbox" size={48} className="mb-3 text-secondary" />
                    <h5 className="fw-bold text-dark">No POD Line Items Found</h5>
                    <p className="text-muted small">No submitted POD line items found for the selected territory and month.</p>
                </div>
            )}

            {/* Media Viewer Modal */}
            <Modal isOpen={attachmentModalOpen} toggle={() => setAttachmentModalOpen(!attachmentModalOpen)} centered size="lg">
                <div className="modal-header p-3 border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="modal-title fw-bold text-dark m-0">POD Attachments</h5>
                    <Button color="link" className="p-0 text-dark border-0 shadow-none" onClick={() => setAttachmentModalOpen(false)}>
                        <FeatherIconCom iconName="X" size={20} />
                    </Button>
                </div>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    {mediaLoading ? (
                        <Loader height="150px" />
                    ) : (
                        <div>
                            <div className="d-flex gap-2 mb-3">
                                <Button
                                    color={selectedMediaTab === 'images' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setSelectedMediaTab('images')}
                                >
                                    Images ({currentMedia.images.length})
                                </Button>
                                <Button
                                    color={selectedMediaTab === 'pdfs' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setSelectedMediaTab('pdfs')}
                                >
                                    PDF Documents ({currentMedia.pdfs.length})
                                </Button>
                            </div>

                            {selectedMediaTab === 'images' && (
                                currentMedia.images.length > 0 ? (
                                    <Row className="g-3">
                                        {currentMedia.images.map((img, i) => (
                                            <Col md={6} key={i}>
                                                <div className="border rounded p-2 text-center bg-light">
                                                    <img src={img.url} alt={`Media ${i}`} style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} />
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : <div className="text-muted text-center py-3">No images available</div>
                            )}

                            {selectedMediaTab === 'pdfs' && (
                                currentMedia.pdfs.length > 0 ? (
                                    <div className="d-flex flex-column gap-2">
                                        {currentMedia.pdfs.map((pdf, i) => (
                                            <div key={i} className="d-flex justify-content-between align-items-center p-3 border rounded bg-light">
                                                <span className="fw-bold text-dark">{pdf.name}</span>
                                                <a href={pdf.pdfurl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
                                                    Open / Download PDF
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                ) : <div className="text-muted text-center py-3">No PDF documents available</div>
                            )}
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
}
